/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable no-case-declarations */
import styled from "styled-components";
import {
  ChangeEvent,
  FC,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  GenericEvent,
  IAdvancedSearchProps,
  ISearchFieldConfig,
  SearchFieldType,
} from "./AdvancedSearch.interface";
import {
  Button,
  ButtonVariant,
  Checkbox,
  CurrencyItem,
  // CreatedByDropdown,
  // CreatedByItemTypes,
  // CreatedByDropdown,
  DatePicker,
  Dropdown,
  Item,
  Search,
  TextInput,
  Typography,
  TypographyVariant,
} from "../ui-kit";
import { DateTime } from "luxon";
import { Filter } from "../icons";

import { QueryParamKeys } from "@shared_packages/toolkit/types";
import { debounce } from "lodash";
import { useQuery } from "@tanstack/react-query";
import api from "../../util/api";
// import { useCreatedByContext } from "../../modules/private/context/CreatedByContextProvider";
import { useGetCompanies } from "../../modules/private/companies/hooks/useGetCompanies";
import { TEST_COMPANY_SCOPE_QUERY } from "../../modules/private/companies/constants/testCompanyScope";
import { SearchInputDropdown } from "./components/SearchInputDropdown";
import { useGetusers } from "../../modules/private/users/hooks/useGetUsers";
import { CurrencyDropdown } from "../ui-kit/input-drop-down/CurrencyDropdown";
import {
  currencyDropdownItems,
  firstCurrencyItemAdvancedSearch,
} from "../../constants/currencies";
// import { SearchInputDropdown } from "./components/SearchInputDropdown";
// import { BrandApiType } from "../../util/types";

const AdvancedFieldsContainer = styled.div<{ active: boolean }>`
  display: grid;
  ${({ active }) =>
    active
      ? "max-height: 2000px; overflow: visible; transition: max-height 0.2s ease-in-out;"
      : "max-height: 0; overflow: hidden;"}
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  column-gap: 1rem;
  row-gap: 0.4rem;
  width: 100%;
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 60px;
  margin-top: 5px;
`;

const RangeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Wrapper = styled.div`
  margin: 20px 0;
  padding: 10px 10px 15px;
  display: flex;
  flex-direction: column;
  border: 1px solid #f8f9fa;
  @media (max-width: 767px) {
    margin: 4px 0 12px;
    padding: 10px 8px 12px;
  }
`;

const StyledSearch = styled(Search)`
  width: 100%;
  @media (max-width: 767px) {
    flex: 1 1 100%;
  }
`;

const CurrencyWrapper = styled.div`
  width: 350px;
  @media (max-width: 767px) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }
`;

const AdvancedButton = styled(Button)<{ active: boolean }>`
  cursor: pointer;
  flex-shrink: 0;
`;

const FilterCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 9px;
  background: #027aff;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
`;

const ClearAllButton = styled(Button)`
  flex-shrink: 0;
  height: auto;
  border: none;
  padding: 0;
  color: #8181a5;
  transition: 0.2s;
  :hover {
    box-shadow: none;
    color: #13273f;
  }
`;

const SEARCH_DEBOUNCE_MS = 500;

const RangeSeparator = styled(Typography)`
  margin: 0 5px;
`;

const ClearButton = styled(Button)`
  height: auto;
  position: absolute;
  top: 58px;
  right: 0;

  border: none;
  padding: 0;
  color: #8181a5;
  transition: 0.2s;
  :hover {
    box-shadow: none;
    color: #13273f;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 767px) {
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const StyledTextInput = styled(TextInput)`
  width: 100%;
`;

export const AdvancedSearch: FC<IAdvancedSearchProps> = ({
  config,
  onChange,
  className,
  isLive = true,
  defaultOpen,
  restoredValues,
  withoutFilterButton,
  searchPlaceholder,
  externalCurrentCurrency,
  externalCurrencyItems,
  disableCurrencyDropdown,
}) => {
  const [userSearch, setUserSearch] = useState<string>();
  const [companySearch, setCompanySearch] = useState<string>();

  const companyField = config.find(
    (c) => c.type === SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN
  );
  const { companies, loading: companyLoading } = companyField
    ? useGetCompanies(
        {
          searchKeyword: companySearch,
          limit: 100,
          testCompanyScope: TEST_COMPANY_SCOPE_QUERY.ALL,
          ...companyField.companyQuery,
        },
        undefined,
        undefined,
        undefined
      )
    : { companies: [], loading: false };

  const { users, loading: userLoading } = config.find(
    (c) => c.type === SearchFieldType.SEARCH_BY_USER_DROPDOWN
  )
    ? useGetusers(
        { searchKeyword: userSearch, limit: 100 },
        undefined,
        undefined,
        undefined
      )
    : { users: [], loading: false };

  const [advancedOpen, setAdvancedOpen] = useState(defaultOpen ?? false);
  const [filtersState, setFiltersState] = useState<Record<string, any> | null>(
    () => {
      const fromParent = restoredValues ? { ...restoredValues } : null;
      if (externalCurrentCurrency) {
        return {
          ...(fromParent ?? {}),
          currency: externalCurrentCurrency.currencyCode,
        };
      }
      return fromParent;
    }
  );

  // Latest filters, readable synchronously from handlers so an immediate emit
  // (dropdown, date) always includes text typed a moment ago that is still
  // waiting in the debounce.
  const filtersRef = useRef<Record<string, any> | null>(filtersState);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // One debounced emitter for the component's lifetime (recreating it per
  // render leaks timers that fire stale values). Text-like fields go through
  // it; everything else emits immediately and cancels any pending text emit —
  // the pending value is already merged into the immediate one.
  const debouncedEmit = useMemo(
    () =>
      debounce((filters: Record<string, any>) => {
        onChangeRef.current(filters);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => debouncedEmit.cancel(), [debouncedEmit]);

  const updateFilters = (
    updater: (prev: Record<string, any>) => Record<string, any>,
    { debounced = false }: { debounced?: boolean } = {}
  ) => {
    const next = updater(filtersRef.current ?? {});
    filtersRef.current = next;
    setFiltersState(next);
    if (debounced) {
      debouncedEmit(next);
    } else {
      debouncedEmit.cancel();
      onChangeRef.current(next);
    }
  };

  const debouncedSetCompanySearch = useMemo(
    () => debounce(setCompanySearch, SEARCH_DEBOUNCE_MS),
    []
  );
  const debouncedSetUserSearch = useMemo(
    () => debounce(setUserSearch, SEARCH_DEBOUNCE_MS),
    []
  );

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    filtersRef.current = restoredValues ?? null;
    setFiltersState(restoredValues ?? null);
  }, [restoredValues]);

  const checked =
    filtersState?.filter === "isSandbox" &&
    (filtersState?.filterValue === true ||
      filtersState?.filterValue === "true");
  const checkedArchived = filtersState?.showDeleted === "true";

  const companyFieldKey = config.find(
    (c) => c.type === SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN
  )?.key;
  const userFieldKey = config.find(
    (c) => c.type === SearchFieldType.SEARCH_BY_USER_DROPDOWN
  )?.key;
  const selectedCompanyId = companyFieldKey
    ? (filtersState?.[companyFieldKey] as string | undefined)
    : undefined;
  const selectedUserId = userFieldKey
    ? (filtersState?.[userFieldKey] as string | undefined)
    : undefined;

  // A restored selection (URL / Back) may not be among the first 100 items
  // the dropdown loads — fetch that one entity so its label can be shown.
  const { data: restoredCompany } = useQuery({
    queryKey: ["advanced-search-company", selectedCompanyId],
    queryFn: () => api.getCompany(selectedCompanyId as string),
    enabled:
      !!selectedCompanyId &&
      !companyLoading &&
      !companies.some((c) => c._id === selectedCompanyId),
    staleTime: Infinity,
  });
  const { data: restoredUser } = useQuery({
    queryKey: ["advanced-search-user", selectedUserId],
    queryFn: () => api.getUserById(selectedUserId as string),
    enabled:
      !!selectedUserId &&
      !userLoading &&
      !users.some((u) => u._id === selectedUserId),
    staleTime: Infinity,
  });

  const mappedCompanies = companies.map((el) => ({
    label: el.tradingName ?? el.name ?? "",
    value: el._id,
  }));
  if (
    restoredCompany &&
    selectedCompanyId &&
    !mappedCompanies.some((c) => c.value === selectedCompanyId)
  ) {
    mappedCompanies.unshift({
      label: restoredCompany.tradingName ?? restoredCompany.name ?? "",
      value: selectedCompanyId,
    });
  }
  mappedCompanies.unshift({ label: "All companies", value: "" });

  const userLabel = (el: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }) =>
    el.email
      ? `${el.firstName} ${el.lastName} (${el.email})`
      : `${el.firstName} ${el.lastName}`;
  const mappedUsers = users.map((el) => ({
    label: userLabel(el),
    value: el._id,
  }));
  if (
    restoredUser &&
    selectedUserId &&
    !mappedUsers.some((u) => u.value === selectedUserId)
  ) {
    mappedUsers.unshift({
      label: userLabel(restoredUser),
      value: selectedUserId,
    });
  }
  mappedUsers.unshift({ label: "All users", value: "" });

  const toggleAdvanced = () => {
    setAdvancedOpen(!advancedOpen);
  };
  const handleValueChange =
    (key: string, type: SearchFieldType) => (event: GenericEvent) => {
      switch (type) {
        case SearchFieldType.TEXT:
        case SearchFieldType.AMOUNT:
        case SearchFieldType.AMOUNT_RANGE:
          updateFilters(
            (prev) => ({
              ...prev,
              [key]: (event as ChangeEvent<HTMLInputElement>).target.value,
            }),
            { debounced: true }
          );
          break;
        case SearchFieldType.DATE:
        case SearchFieldType.DATE_RANGE:
          updateFilters((prev) => ({
            ...prev,
            [key]: (event as DateTime)?.toISO(),
          }));
          break;
        case SearchFieldType.DROPDOWN:
        case SearchFieldType.CREATED_BY_DROPDOWN:
        case SearchFieldType.CATEGORY_DROPDOWN:
        case SearchFieldType.SUPPLIER_DROPDOWN:
        case SearchFieldType.ROLE_DROPDOWN:
        case SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN:
        case SearchFieldType.SEARCH_BY_USER_DROPDOWN:
        case SearchFieldType.SHIPPING_COMPANY_DROPDOWN:
        case SearchFieldType.PICKER_DROPDOWN:
        case SearchFieldType.LOCATION_DROPDOWN:
        case SearchFieldType.TERMINAL_DROPDOWN:
        case SearchFieldType.SEARCHABLE_DROPDOWN:
          updateFilters((prev) => ({ ...prev, [key]: (event as Item).value }));
          break;
        case SearchFieldType.CURRENCY_DROPDOWN:
          updateFilters((prev) => ({
            ...prev,
            [key]: (event as CurrencyItem).currencyCode,
          }));
          break;
        case SearchFieldType.CHECKBOX: {
          // Unchecked means "no sandbox filter", not "filterValue=false":
          // the backend treats any filterValue as a constraint.
          const isChecked = (event as ChangeEvent<HTMLInputElement>).target
            .checked;
          updateFilters((prev) => {
            const { filter, filterValue, ...rest } = prev;
            return isChecked
              ? { ...rest, filter: "isSandbox", filterValue: true }
              : rest;
          });
          break;
        }
        case SearchFieldType.CHECKBOX_ARCHIVED: {
          const isCheckedArchived = (event as ChangeEvent<HTMLInputElement>)
            .target.checked;
          updateFilters((prev) => {
            const { showDeleted, ...rest } = prev;
            return isCheckedArchived ? { ...rest, showDeleted: "true" } : rest;
          });
          break;
        }
      }
    };

  const handleSearchInputChange = (text: string) => {
    updateFilters(
      (prev) => ({ ...prev, [QueryParamKeys.SEARCH_KEYWORD]: text.trim() }),
      { debounced: true }
    );
  };

  const handleClear = (c: ISearchFieldConfig) => {
    updateFilters((prev) => {
      const {
        [c.key]: omitKey,
        [c.rangeEndKey ?? ""]: omitRangeEndKey,
        ...rest
      } = prev;
      return rest;
    });
  };

  // Keys a config field writes into the filters object.
  const fieldKeys = (c: ISearchFieldConfig): string[] => {
    switch (c.type) {
      case SearchFieldType.CHECKBOX:
        return ["filter", "filterValue"];
      case SearchFieldType.CHECKBOX_ARCHIVED:
        return ["showDeleted"];
      default:
        return c.rangeEndKey ? [c.key, c.rangeEndKey] : [c.key];
    }
  };

  const hasValue = (v: unknown) =>
    v !== undefined && v !== null && v !== "" && v !== false;

  // A dropdown sitting on its default item is not an "applied" filter.
  const dropdownDefault = (c: ISearchFieldConfig) =>
    c.key === "testCompanyScope" ? TEST_COMPANY_SCOPE_QUERY.NON_TEST : "";

  const fieldIsActive = (c: ISearchFieldConfig) => {
    switch (c.type) {
      case SearchFieldType.CHECKBOX:
        return checked;
      case SearchFieldType.DROPDOWN: {
        const v = filtersState?.[c.key];
        return hasValue(v) && v !== dropdownDefault(c);
      }
      default:
        return fieldKeys(c).some((k) => hasValue(filtersState?.[k]));
    }
  };

  const advancedFields = config.filter(
    (c) => c.type !== SearchFieldType.CURRENCY_DROPDOWN
  );
  const activeFilterCount = advancedFields.filter(fieldIsActive).length;

  // Clears the collapsible fields only; the top search and currency stay.
  const handleClearAll = () => {
    const keysToDrop = new Set(advancedFields.flatMap(fieldKeys));
    updateFilters((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([k]) => !keysToDrop.has(k))
      )
    );
  };

  // Not memoized: must re-render when mappedCompanies / mappedUsers update
  // after async search, otherwise SearchInputDropdown keeps a stale items list.
  const renderField = (c: ISearchFieldConfig) => {
    {
      switch (c.type) {
        case SearchFieldType.AMOUNT:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <TextInput
                placeholder={c.placeholder}
                value={filtersState?.[c.key] ?? ""}
                onChange={handleValueChange(c.key, c.type)}
              />
            </FieldWrapper>
          );
        case SearchFieldType.AMOUNT_RANGE:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <RangeWrapper>
                <TextInput
                  placeholder={c.placeholder}
                  // type="number"
                  value={filtersState?.[c.key.toString()] || ""}
                  onChange={handleValueChange(c.key, c.type)}
                />
                <RangeSeparator variant={TypographyVariant.BODY1}>
                  -
                </RangeSeparator>
                <TextInput
                  value={filtersState?.[c.rangeEndKey!.toString()] || ""}
                  placeholder={c.rangeEndPlaceholder}
                  onChange={handleValueChange(c.rangeEndKey!, c.type)}
                />
              </RangeWrapper>
              <ClearButton
                variant={ButtonVariant.TEXT}
                onClick={() => {
                  handleClear(c);
                }}
              >
                clear
              </ClearButton>
            </FieldWrapper>
          );
        case SearchFieldType.DATE:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <DatePicker
                placeholder={c.placeholder}
                value={filtersState?.[c.key]}
                onChange={handleValueChange(c.key, c.type)}
              />
            </FieldWrapper>
          );
        case SearchFieldType.DATE_RANGE:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <RangeWrapper>
                <DatePicker
                  startOfDayWhenSelecting
                  value={filtersState?.[c.key.toString()]}
                  placeholder={c.placeholder}
                  onChange={handleValueChange(c.key, c.type)}
                />
                <RangeSeparator variant={TypographyVariant.BODY1}>
                  -
                </RangeSeparator>
                <DatePicker
                  endOfDayWhenSelecting
                  value={filtersState?.[c?.rangeEndKey?.toString() ?? ""]}
                  placeholder={c.rangeEndPlaceholder}
                  onChange={handleValueChange(c.rangeEndKey!, c.type)}
                />
              </RangeWrapper>
              <ClearButton
                variant={ButtonVariant.TEXT}
                onClick={() => {
                  handleClear(c);
                }}
              >
                clear
              </ClearButton>
            </FieldWrapper>
          );
        case SearchFieldType.TEXT:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <StyledTextInput
                placeholder={c.placeholder}
                value={filtersState?.[c.key] ?? ""}
                onChange={handleValueChange(c.key, c.type)}
              />
            </FieldWrapper>
          );
        case SearchFieldType.DROPDOWN: {
          const raw = filtersState?.[c?.key?.toString()];
          const defaultValue =
            raw !== undefined && raw !== null && raw !== ""
              ? raw
              : dropdownDefault(c);
          const defaultItem =
            c?.dropdownValues?.find((item) => item.value === defaultValue) ??
            undefined;

          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <Dropdown
                key={`${c.key}-${defaultValue}`}
                defaultItem={defaultItem}
                fullWidth
                items={c.dropdownValues ?? []}
                onChange={handleValueChange(c.key, c.type)}
              />
            </FieldWrapper>
          );
        }
        case SearchFieldType.CREATED_BY_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <CreatedByDropdown
                items={createdByValues ?? []}
                onChange={handleValueChange(c.key, c.type)}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <SearchInputDropdown
                items={mappedCompanies ?? []}
                value={filtersState?.[c.key] ?? ""}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={debouncedSetCompanySearch}
                loading={companyLoading}
              />
            </FieldWrapper>
          );
        case SearchFieldType.SEARCHABLE_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <SearchInputDropdown
                items={c.dropdownValues ?? []}
                value={filtersState?.[c.key] ?? ""}
                onChange={handleValueChange(c.key, c.type)}
              />
            </FieldWrapper>
          );
        case SearchFieldType.SEARCH_BY_USER_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              <SearchInputDropdown
                items={mappedUsers ?? []}
                value={filtersState?.[c.key] ?? ""}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={debouncedSetUserSearch}
                loading={userLoading}
              />
            </FieldWrapper>
          );
        case SearchFieldType.CATEGORY_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <SearchInputDropdown
                items={mappedCategories ?? []}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={setCategorySearch}
                loading={categoryLoading}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.SUPPLIER_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <SearchInputDropdown
                items={mappedSuppliers ?? []}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={setSupplierSearch}
                loading={supplierLoading}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.LOCATION_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <SearchInputDropdown
                items={mappedLocations ?? []}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={setLocationSearch}
                loading={locationLoading}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.TERMINAL_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <SearchInputDropdown
                items={mappedTerminals ?? []}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={setLocationSearch}
                loading={terminalLoading}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.SHIPPING_COMPANY_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <Dropdown
                fullWidth
                items={mappedCarriers ?? []}
                onChange={handleValueChange(c.key, c.type)}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.BRAND_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <BrandsDropdown
                loading={brandLoading}
                brands={brands}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                externalSearchHandler={setBrandSearch}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.ROLE_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <CreatedByDropdown
                withoutAvatar
                items={mappedRoles ?? []}
                onChange={handleValueChange(c.key, c.type)}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.PICKER_DROPDOWN:
          return (
            <FieldWrapper>
              <Typography variant={TypographyVariant.BODY4}>
                {c.label}
              </Typography>
              {/* <SearchInputDropdown
                items={mappedPickers ?? []}
                onChange={handleValueChange(c.key, c.type)}
                externalSearchHandler={setPickerSearch}
                loading={pickerLoading}
              /> */}
            </FieldWrapper>
          );
        case SearchFieldType.CHECKBOX:
          return (
            <FieldWrapper>
              <Checkbox
                checked={checked}
                onChange={handleValueChange(c.key, c.type)}
                label={
                  <Typography variant={TypographyVariant.BODY4}>
                    {c.label}
                  </Typography>
                }
              />
            </FieldWrapper>
          );
        case SearchFieldType.CHECKBOX_ARCHIVED:
          return (
            <FieldWrapper>
              <Checkbox
                checked={checkedArchived}
                onChange={handleValueChange(c.key, c.type)}
                label={
                  <Typography variant={TypographyVariant.BODY4}>
                    {c.label}
                  </Typography>
                }
              />
            </FieldWrapper>
          );
      }
    }
    return null;
  };

  // Re-emit restored filters once so the parent's hook applies them. Pages
  // without restoredValues already hold their own initial state — emitting
  // `undefined` there only caused a `setFilters(undefined)` and a second fetch.
  useEffect(() => {
    if (restoredValues !== undefined) {
      onChangeRef.current(restoredValues);
    }
  }, []);

  const foundCurrencyConfig = config.find(
    (c) => c.type === SearchFieldType.CURRENCY_DROPDOWN
  );
  const hasAdvancedFields = advancedFields.length > 0;

  return (
    <Wrapper className={className}>
      <Row>
        <StyledSearch
          onChangeHandler={handleSearchInputChange}
          value={(filtersState?.searchKeyword as string) ?? ""}
          isLive={isLive}
          placeholder={searchPlaceholder}
        />
        {foundCurrencyConfig && (
          <CurrencyWrapper>
            <CurrencyDropdown
              isInAdvancedSearch
              value={externalCurrentCurrency ?? firstCurrencyItemAdvancedSearch}
              uniqueId="advancedSearchCurrencyDropdown"
              onChange={handleValueChange(
                foundCurrencyConfig.key,
                foundCurrencyConfig.type
              )}
              customCurrencyItems={
                externalCurrencyItems ?? currencyDropdownItems
              }
              disabled={disableCurrencyDropdown}
            />
          </CurrencyWrapper>
        )}
        {!withoutFilterButton && hasAdvancedFields && (
          <AdvancedButton
            leftIcon={<Filter />}
            active={advancedOpen}
            onClick={toggleAdvanced}
            variant={ButtonVariant.OUTLINED}
          >
            Filter
            {activeFilterCount > 0 && (
              <FilterCountBadge aria-label={`${activeFilterCount} active`}>
                {activeFilterCount}
              </FilterCountBadge>
            )}
          </AdvancedButton>
        )}
        {hasAdvancedFields && activeFilterCount > 0 && (
          <ClearAllButton variant={ButtonVariant.TEXT} onClick={handleClearAll}>
            Clear all
          </ClearAllButton>
        )}
      </Row>
      {/* {advancedOpen && ( */}
      <AdvancedFieldsContainer active={advancedOpen}>
        {config.map((c) => (
          <Fragment key={c.key}>{renderField(c)}</Fragment>
        ))}
      </AdvancedFieldsContainer>
      {/* )} */}
    </Wrapper>
  );
};
